import {GRANITE_1, GRANITE_4} from '@gaia/colors';
import Button from '@gaia/components/Button';
import type {UserLayerDispatcher} from '@gaia/hooks/useLayers';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {fetchDefaultLayers, HuntingStates} from '@gaia/utils/gaia/fetchDefaultUserLayers';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import useGetSyncPromise from '@gaia/utils/react/useGetSyncPromise';
import SubscriptionTooltip from '@map/SubscriptionTooltip';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import {
    Avatar,
    CircularProgress,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from '@mui/material';
import sortBy from 'lodash/sortBy';
import {useEffect, useState} from 'react';

import HuntingMaps from './HuntingMaps';
import type {OnboardingPopupProps} from './OnboardingPopup';

interface AddMapProps extends OnboardingPopupProps {
    selectedActivities: string[];
    setStepCount: SetState<number>;
}

export default function AddMaps({
    selectedActivities,
    userLayerDispatcher,
    layerTypes,
    userLayers,
    onSidebarChange,
    setStepCount,
}: AddMapProps) {
    const getSyncPromise = useGetSyncPromise();
    const categoryIds = new Set(
        selectedActivities.map((activity) => {
            const activityIndex = activity.indexOf('.');
            // activities are formatted as 'category.activity'
            if (activityIndex > -1) {
                return activity.slice(0, activityIndex);
            } else {
                return activity;
            }
        })
    );

    const [layerDefaults, , isLoaded] = getSyncPromise(
        fetchDefaultLayers(useUser().canAccessNonPublicSources, Array.from(categoryIds))
    );
    const [displayedLayerList, setDisplayedLayerList] = useState(['GaiaTopoRasterFeet']);
    const [huntingStates, setHuntingStates] = useState<HuntingStates[] | undefined>();
    useEffect(() => {
        //Once fetchDefaultLayers promise is resolved,
        //update displayedLayerList and huntingStates with a list of map sources
        if (layerDefaults && layerDefaults.sources.length > 0) {
            setDisplayedLayerList(layerDefaults.sources);
        }
        if (layerDefaults && layerDefaults.huntingStates.length > 0) {
            setHuntingStates(sortBy(layerDefaults.huntingStates, 'state'));
        }
    }, [layerDefaults, setHuntingStates, setDisplayedLayerList]);
    const recommendedLayers = sortBy(layerTypes, [(layerType: LayerType) => layerType.rank]).filter(
        (layerType) => !layerType.deprecated && displayedLayerList.includes(layerType.id)
    );

    if (!isLoaded) {
        return <CircularProgress thickness={7} size={24} disableShrink color='inherit' />;
    }
    return (
        <>
            <Typography variant='h5' style={{marginTop: theme.spacing(2)}}>
                Maps recommended for you.
            </Typography>
            <Typography variant='body1' style={{marginTop: theme.spacing(1)}}>
                Add or remove maps to get started.
            </Typography>
            <Typography variant='caption'>
                Premium Maps require a membership - you can add them now and decide whether to
                upgrade later.
            </Typography>
            <div style={{overflow: 'scroll', height: 275}}>
                {huntingStates && (
                    <HuntingMaps
                        huntingStates={huntingStates}
                        setDisplayedLayerList={setDisplayedLayerList}
                        layerDefaults={layerDefaults}
                    />
                )}
                {recommendedLayers.map((layer) => {
                    const isSelected = userLayers?.find(
                        (userLayer: UserLayer) => layer.id === userLayer.layer.type
                    );
                    return (
                        <ListItem key={layer.id}>
                            <ListItemAvatar>
                                <Avatar>
                                    {!layer.iconURL ? (
                                        <div style={{color: GRANITE_1}}>
                                            {layer.name.substr(0, 1).toUpperCase()}
                                        </div>
                                    ) : (
                                        <img
                                            src={layer.iconURL || ''}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                            }}
                                            alt={''}
                                        />
                                    )}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={layer.name}
                                secondary={<>{layer.attribution}</>}
                            />
                            {layer.subscriptionDataset === 'gaiagreen' && (
                                <SubscriptionTooltip
                                    placement='right'
                                    title='This map requires a Premium Membership.'
                                    subscription={undefined}
                                >
                                    <Typography variant='overline' style={{color: GRANITE_4}}>
                                        PREMIUM
                                    </Typography>
                                </SubscriptionTooltip>
                            )}
                            {!layer.id.includes('GaiaTopo') &&
                                (isSelected ? (
                                    <RemoveMapButton
                                        layer={layer}
                                        userLayerDispatcher={userLayerDispatcher}
                                    />
                                ) : (
                                    <AddMapButton
                                        layer={layer}
                                        userLayerDispatcher={userLayerDispatcher}
                                    />
                                ))}
                        </ListItem>
                    );
                })}
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: theme.spacing(2),
                }}
            >
                <Button
                    appearance='secondary'
                    onClick={() => setStepCount((prevState: number) => prevState - 1)}
                >
                    Back
                </Button>
                <Button
                    appearance='primary'
                    onClick={() => {
                        onSidebarChange({type: 'layers'});
                        setStepCount((prevState: number) => prevState + 1);
                    }}
                >
                    Next
                </Button>
            </div>
        </>
    );
}

interface MapButtonsProps {
    layer: LayerType;
    userLayerDispatcher: UserLayerDispatcher;
}

function AddMapButton({layer, userLayerDispatcher}: MapButtonsProps) {
    return (
        <IconButton
            edge='end'
            onClick={() => {
                userLayerDispatcher({type: 'add', layer: layer});
                ampli.addMapInWebOnboarding({
                    mapSource: layer.name,
                    label: 'web onboarding',
                });
            }}
            color='primary'
            size='large'
        >
            <AddCircleIcon style={{width: 24, height: 24}} />
        </IconButton>
    );
}

function RemoveMapButton({layer, userLayerDispatcher}: MapButtonsProps) {
    return (
        <IconButton
            edge='end'
            onClick={() => userLayerDispatcher({type: 'remove', layer: layer})}
            color='error'
            size='large'
        >
            <RemoveCircleIcon style={{width: 24, height: 24}} />
        </IconButton>
    );
}
